import React, { useState, useMemo } from 'react';
import {
  Box,
  Modal,
  Typography,
  Button,
  Paper,
  Stepper,
  Step,
  StepLabel,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  Grid,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DataGrid } from '@mui/x-data-grid';
import { useDashboardContext } from '../../contexts/DashboardContext';
import { PDFDownloadLink } from '@react-pdf/renderer';
import ReportPDFDocument from './ReportPDFDocument';
import { getTrialByTrialId, getSummaryDataByTrialId } from '../../utils/fastAPI'

const ReportGenerator = ({ open, onClose, patientName }) => {
  const { allTrials, clinicianInfo, userName } = useDashboardContext();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedTrialIds, setSelectedTrialIds] = useState([]);
  const [reportData, setReportData] = useState({
    objectiveStatement: '',
    assessmentStatement: '',
    aiAssessment: '',
    patientFeedback: {
      painLevel: 0,
      notes: ''
    },
    planStatement: '',
    otherNotes: '',
    assistiveDeviceUsed: false,
    assistiveDeviceType: ''
  });
  const [showPdfModal, setShowPdfModal] = useState(false);

  const [selectionModel, setSelectionModel] = useState([]);
  const [assistiveDeviceInfo, setAssistiveDeviceInfo] = useState('');

  const columns = [
    { field: "date", headerName: "Date", flex: 1, sortable: true },
    {
      field: "parsedStartTimeString",
      headerName: "Start Time",
      flex: 1,
      sortable: true,
    },
    { 
      field: "deviceSide", 
      headerName: "Leg", 
      flex: 1, 
      sortable: false,
      valueGetter: (params) => {
        if (params.row.deviceFirmwareVersion) {
          const side = params.row.deviceFirmwareVersion.trim();
          return side.charAt(0).toUpperCase() + side.slice(1).toLowerCase();
        }
        return 'N/A';
      }
    },
    { field: "sessionType", headerName: "Trial Type", flex: 1, sortable: false },
    {
      field: "durationFormatted",
      headerName: "Duration",
      flex: 1,
      sortable: true,
    },
  ];

  const processedData = useMemo(() => {
    return (allTrials || []).map(trial => {
      let parsedStartTimeString = 'N/A';
      let parsedEndTimeString = 'N/A';
      let fullStartDateTime = null;
      let fullEndDateTime = null;
      let durationFormatted = 'N/A';
      let durationTotalSeconds = null;

      if (trial.timePeriod && typeof trial.timePeriod === 'string') {
        const parts = trial.timePeriod.split(' - ');
        if (parts[0]) {
          parsedStartTimeString = parts[0];
        }
        if (parts[1]) {
          parsedEndTimeString = parts[1];
        }
      }

      if (trial.date && parsedStartTimeString !== 'N/A') {
        const datePart = new Date(trial.date);
        if (!isNaN(datePart.getTime())) {
          const [hours, minutes, seconds] = parsedStartTimeString.split(':').map(Number);
          if (![hours, minutes, seconds].some(isNaN)) {
            fullStartDateTime = new Date(datePart.getFullYear(), datePart.getMonth(), datePart.getDate(), hours, minutes, seconds);
          }
        }
      }

      if (trial.date && parsedEndTimeString !== 'N/A') {
         const datePart = new Date(trial.date);
         if(!isNaN(datePart.getTime())){
            const [hours, minutes, seconds] = parsedEndTimeString.split(':').map(Number);
            if (![hours, minutes, seconds].some(isNaN)) {
              fullEndDateTime = new Date(datePart.getFullYear(), datePart.getMonth(), datePart.getDate(), hours, minutes, seconds);
            }
         }
      }

      if (!fullStartDateTime && trial.startTimestamp) {
          const st = new Date(trial.startTimestamp);
          if (!isNaN(st.getTime())) {
            fullStartDateTime = st;
            if (parsedStartTimeString === 'N/A') {
              parsedStartTimeString = st.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
            }
          }
      }
      if (!fullEndDateTime && trial.endTimestamp) {
          const et = new Date(trial.endTimestamp);
          if (!isNaN(et.getTime())) fullEndDateTime = et;
      }

      if (fullStartDateTime && fullEndDateTime && fullEndDateTime.getTime() > fullStartDateTime.getTime()) {
        const durationMs = fullEndDateTime.getTime() - fullStartDateTime.getTime();
        durationTotalSeconds = Math.round(durationMs / 1000);
        const minutes = Math.floor(durationTotalSeconds / 60);
        const seconds = durationTotalSeconds % 60;
        if (minutes > 0) {
          durationFormatted = `${minutes}min ${seconds}s`;
        } else if (seconds > 0) {
          durationFormatted = `${seconds}s`;
        } else {
            durationFormatted = '0s';
        }
      }
      
      return {
        ...trial,
        id: trial.id,
        parsedStartTimeString,
        durationFormatted,
        durationTotalSeconds,
        fullStartDateTime: fullStartDateTime ? fullStartDateTime.toISOString() : null,
      };
    });
  }, [allTrials]);

  const handleNext = () => {
    if (activeStep === 0 && selectionModel.length === 0) {
      alert('Please select at least one trial to continue');
      return;
    }
    setSelectedTrialIds(selectionModel);
    
    // Pre-fill the objective statement with generated text while still allowing user edits
    if (activeStep === 0) {
      setReportData(prev => ({
        ...prev,
        objectiveStatement: generateObjectiveStatement(selectionModel)
      }));
    }
    
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleInputChange = (section, field, value) => {
    if (section) {
      setReportData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    } else {
      setReportData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const resetForm = () => {
    setActiveStep(0);
    setSelectedTrialIds([]);
    setSelectionModel([]);
    setReportData({
      objectiveStatement: '',
      assessmentStatement: '',
      aiAssessment: '',
      patientFeedback: {
        painLevel: 0,
        notes: ''
      },
      planStatement: '',
      otherNotes: '',
      assistiveDeviceUsed: false,
      assistiveDeviceType: ''
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSelectionChange = (newSelectionModel) => {
    setSelectionModel(newSelectionModel);
  };

  const steps = ['Select Trials', 'Create Report'];

  const generateObjectiveStatement = (trialIds = selectedTrialIds) => {
    if (trialIds.length === 0) return '';

    const selectedFullTrials = processedData.filter(trial => trialIds.includes(trial.id));
    
    const sortedTrials = [...selectedFullTrials].sort((a, b) => {
      const timeA = a.fullStartDateTime ? new Date(a.fullStartDateTime).getTime() : 0;
      const timeB = b.fullStartDateTime ? new Date(b.fullStartDateTime).getTime() : 0;
      return timeA - timeB;
    });

    let statement = '';
    
    sortedTrials.forEach((trial, index) => {
      const startTime = trial.parsedStartTimeString || 'N/A';
      const trialType = trial.sessionType || 'N/A';
      
      // Get leg side from deviceFirmwareVersion
      let legSide = 'N/A';
      if (trial.deviceFirmwareVersion) {
        const side = trial.deviceFirmwareVersion.trim();
        legSide = side.charAt(0).toUpperCase() + side.slice(1).toLowerCase();
      }
      
      const duration = trial.durationFormatted || 'N/A duration';
      
      // Parse stim data
      let stimDescription = 'without FES stimulation';
      if (trial.stim && trial.stim.length > 0) {
        const activeChannels = trial.stim.filter(channel => channel.turnOn === 1);
        
        if (activeChannels.length > 0) {
          stimDescription = 'with FES stimulating ';
          
          activeChannels.forEach((channel, chIndex) => {
            const muscle = channel.muscle || 'unknown muscle';
            const stimLevel = channel.stimLevel || 0;
            const startTiming = channel.startTiming || 0;
            const stopTiming = channel.stopTiming || 0;
            
            if (chIndex > 0) {
              stimDescription += ' and ';
            }
            
            stimDescription += `${muscle} at level ${stimLevel} (${startTiming}-${stopTiming}% of gait cycle)`;
          });
        }
      }
      
      if (index === 0) {
        statement += `First, the patient performed a ${trialType} at ${startTime} during ${duration} ${stimDescription} on ${legSide} leg.`;
      } else {
        statement += `\n\nThen, the patient performed a ${trialType} at ${startTime} during ${duration} ${stimDescription} on ${legSide} leg.`;
      }
    });

    return statement;
  };
    
  

  const generateAIAssessment = async () => {
    try {
      // Get the selected trials data
      const selectedTrials = processedData.filter(trial => selectedTrialIds.includes(trial.id));
      if (selectedTrials.length === 0) {
        setReportData(prev => ({
          ...prev,
          aiAssessment: 'Please select at least one trial to generate an assessment.'
        }));
        return;
      }

      // Show loading state
      setReportData(prev => ({
        ...prev,
        aiAssessment: 'Generating assessment...'
      }));

      // Import the FastAPI function
      const { compareTrials } = await import('../../utils/fastAPI');

      // Sort trials chronologically (earliest first, latest last)
      const sortedTrials = [...selectedTrials].sort((a, b) => {
        const timeA = a.fullStartDateTime ? new Date(a.fullStartDateTime).getTime() : 
                     (a.date ? new Date(a.date).getTime() : 0);
        const timeB = b.fullStartDateTime ? new Date(b.fullStartDateTime).getTime() : 
                     (b.date ? new Date(b.date).getTime() : 0);
        return timeA - timeB;
      });

      // Prepare trial info for the API call with sequential labeling
      const trialInfoArray = sortedTrials.map((trial, index) => {

        let legSide = 'N/A';
        if (trial.stim && trial.stim.length > 0 && trial.stim[0].deviceLocation) {
          const deviceLocation = trial.stim[0].deviceLocation;
          if (deviceLocation === 'LL') legSide = 'Left';
          else if (deviceLocation === 'RR') legSide = 'Right';
          else legSide = deviceLocation;
        } else {
          legSide = trial.deviceLocation || trial.deviceSide || 'N/A';
        }

        let stimDescription = 'without FES stimulation';
        if (trial.stim && trial.stim.length > 0) {
          const activeChannels = trial.stim.filter(channel => channel.turnOn === 1);
          
          if (activeChannels.length > 0) {
            stimDescription = 'with FES stimulating ';
            
            activeChannels.forEach((channel, chIndex) => {
              const muscle = channel.muscle || 'unknown muscle';
              const stimLevel = channel.stimLevel || 0;
              const startTiming = channel.startTiming || 0;
              const stopTiming = channel.stopTiming || 0;
              
              if (chIndex > 0) {
                stimDescription += ' and ';
              }
              
              stimDescription += `${muscle} at level ${stimLevel} (${startTiming}-${stopTiming}% of gait cycle)`;
            });
          }
        }
        // Extract relevant trial info
        return {
          trialLabel: `Trial ${index + 1}`,  // Add sequential labeling
          id: trial.id,
          date: trial.date,
          sessionType: trial.sessionType,
          deviceSide: legSide,
          duration: trial.durationFormatted,
          stimulation: stimDescription
        };
      });
  

      // Create an array to store all the promises for fetching summary data
      const summaryDataPromises = sortedTrials.map(trial => getSummaryDataByTrialId(trial.id));
      
      // Wait for all summary data to be fetched
      const summaryDataResults = await Promise.all(summaryDataPromises);
      
      // Extract and format summary data for each trial with sequential labeling
      const trialSummaryArray = sortedTrials.map((trial, index) => {
        // Get the summary data for this trial
        const summaryData = summaryDataResults[index] || {};
        
        // Format the summary data similar to the AnalyzeTrial component
        return {
          trialLabel: `Trial ${index + 1}`,  // Add sequential labeling
          id: trial.id,
          date: trial.date,
          sessionType: trial.sessionType || 'N/A',
          summaryData: {
            "Steps": summaryData.peak_step_count ? `${handleDecimals(summaryData.peak_step_count)} steps` : 'N/A',
            "Walking Time": summaryData.walking_time ? `${handleDecimals(summaryData.walking_time)} s` : 'N/A',
            "Speed": summaryData.speed ? `${handleDecimals(summaryData.speed)} m/s` : 'N/A',
            "Distance": summaryData.distance ? `${handleDecimals(summaryData.distance)} m` : 'N/A',
            "Cadence": summaryData.cadence ? `${handleDecimals(summaryData.cadence)} / min` : 'N/A',
            "Stride Length": summaryData.step_length ? `${handleDecimals(summaryData.step_length)} m` : 'N/A',
            "Swing %": summaryData.swingRatio ? `${handleDecimals(summaryData.swingRatio)} %` : 'N/A',
            "Stance %": summaryData.stanceRatio ? `${handleDecimals(summaryData.stanceRatio)} %` : 'N/A',
            "Knee Flex° Mid Stance": summaryData.midstanceKnee ? `${handleDecimals(summaryData.midstanceKnee)} °` : 'N/A',
            "Knee Flex° Initial Swing": summaryData.initialswingKnee ? `${handleDecimals(summaryData.initialswingKnee)} °` : 'N/A',
            "Knee Flex° Mid Swing": summaryData.midswingKnee ? `${handleDecimals(summaryData.midswingKnee)} °` : 'N/A',
            "Total Knee ROM°": summaryData.romKnee ? `${handleDecimals(summaryData.romKnee)} °` : 'N/A',
          }
        };
      });

      // Create a comparison prompt with chronological trial ordering
      const conditionDescription = `Comparison of ${sortedTrials.length} walking trials for patient ${patientName}, ordered chronologically from earliest (Trial 1) to most recent (Trial ${sortedTrials.length}).`;
      
      console.log("trialInfoArray", trialInfoArray);
      console.log("trialSummaryArray", trialSummaryArray);
      console.log("conditionDescription", conditionDescription);
      
      // Make the API call to backend for AI analysis
      const assessment = await compareTrials(trialInfoArray, trialSummaryArray, conditionDescription);
      
      // Update the UI with the generated assessment
      setReportData(prev => ({
        ...prev,
        aiAssessment: assessment
      }));
    } catch (error) {
      console.error('Error generating AI assessment:', error);
      setReportData(prev => ({
        ...prev,
        aiAssessment: 'An error occurred while generating the assessment. Please try again.'
      }));
    }
  };

  // Helper function to handle decimal formatting
  const handleDecimals = (value) => {
    if (value === undefined || value === null) return "N/A";
    return Number.isInteger(value) ? value.toString() : value.toFixed(2);
  };

  const handleGenerateReport = () => {
    // Prepare final report data with assistive device information for PDF
    const deviceInfo = reportData.assistiveDeviceUsed 
      ? `The patient performed these tests with ${reportData.assistiveDeviceType || 'an assistive device'}.`
      : 'The patient performed these tests without an assistive device.';
      
    // Set assistive device info to state so it's available for the PDF
    setAssistiveDeviceInfo(deviceInfo);
    
    // Log complete report data
    console.log('Report Data:', {
      ...reportData,
      assistiveDeviceInfo: deviceInfo
    });
    
    // Show the PDF download modal
    setShowPdfModal(true);
  };

  const handleClosePdfModal = () => {
    setShowPdfModal(false);
    handleClose();
  };

  // Get clinician name from localStorage as this is where it's stored when the user logs in
  const clinicianName = useMemo(() => {
    // First try to get it from localStorage (this is where the sidebar gets it from)
    const storedName = localStorage.getItem('name');
    if (storedName && storedName.trim() !== '' && storedName !== 'undefined') {
      return storedName;
    }
    
    // Fall back to other sources if localStorage doesn't have it
    if (clinicianInfo && clinicianInfo.firstName && clinicianInfo.lastName) {
      return `${clinicianInfo.firstName} ${clinicianInfo.lastName}`;
    }
    
    if (userName && userName.trim() !== '') {
      return userName;
    }
    
    return "Clinician";
  }, [clinicianInfo, userName]);

  // Ensure patientName is properly used
  const formattedPatientName = useMemo(() => {
    if (patientName && patientName.trim() !== '' && patientName !== 'undefined') {
      return patientName;
    }
    return "Patient";
  }, [patientName]);

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="report-generator-modal"
      >
        <Paper
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: '80%', md: '70%' },
            maxHeight: '90vh',
            overflow: 'auto',
            p: 4,
            boxShadow: 24,
            borderRadius: 2
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5" component="h2">
              Create Report for {patientName}
            </Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {activeStep === 0 ? (
            <Box>
              <Typography variant="h6" mb={2}>
                Select one or more trials to include in the report:
              </Typography>
              <Box sx={{ height: 400, width: '100%' }} mb={3}>
                <DataGrid
                  rows={processedData}
                  columns={columns}
                  getRowId={(row) => row.id}
                  checkboxSelection
                  disableRowSelectionOnClick
                  onRowSelectionModelChange={handleSelectionChange}
                  rowSelectionModel={selectionModel}
                  initialState={{
                    sorting: {
                      sortModel: [{ field: 'fullStartDateTime', sort: 'desc' }],
                    },
                    pagination: {
                      paginationModel: { pageSize: 5 },
                    },
                  }}
                  pageSizeOptions={[5, 10, 25]}
                />
              </Box>
            </Box>
          ) : (
            <Box>
              <Typography variant="h6" gutterBottom>
                Report Details
              </Typography>
              
              <Box mb={4}>
                <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                  Objective Statement
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={10}
                  variant="outlined"
                  value={reportData.objectiveStatement}
                  onChange={(e) => handleInputChange(null, 'objectiveStatement', e.target.value)}
                />
              </Box>

              <Box mb={4}>
                <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                  Assessment Statement
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  placeholder="Ex: Patient performed step up activity with FES stimulating glut max and quads to improve hip and knee stability during stance phase of gait"
                  value={reportData.assessmentStatement}
                  onChange={(e) => handleInputChange(null, 'assessmentStatement', e.target.value)}
                />
              </Box>

              <Box mb={4}>
                <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                  Assessment of Gait
                </Typography>
                <Box display="flex" alignItems="center" mb={2}>
                  <Button variant="contained" onClick={generateAIAssessment} sx={{ mr: 2 }}>
                    Generate Assessment
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    (Analyze EvoWalk data using AI)
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  minRows={5}
                  maxRows={20}
                  variant="outlined"
                  value={reportData.aiAssessment}
                  onChange={(e) => handleInputChange(null, 'aiAssessment', e.target.value)}
                  InputProps={{ 
                    readOnly: true,
                    sx: { 
                      height: 'auto', 
                      overflowY: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordWrap: 'break-word' 
                    } 
                  }}
                />
              </Box>

              <Box mb={4}>
                <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                  Patient Feedback
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth>
                      <InputLabel id="pain-level-label">Pain Level (0-10)</InputLabel>
                      <Select
                        labelId="pain-level-label"
                        value={reportData.patientFeedback.painLevel}
                        label="Pain Level (0-10)"
                        onChange={(e) => handleInputChange('patientFeedback', 'painLevel', e.target.value)}
                      >
                        {[...Array(11).keys()].map((num) => (
                          <MenuItem key={num} value={num}>{num}/10</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      label="Notes"
                      variant="outlined"
                      value={reportData.patientFeedback.notes}
                      onChange={(e) => handleInputChange('patientFeedback', 'notes', e.target.value)}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box mb={4}>
                <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                  Plan Statement
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  variant="outlined"
                  placeholder="Ex: Continue frequency and intensity of FES stimulation..."
                  value={reportData.planStatement}
                  onChange={(e) => handleInputChange(null, 'planStatement', e.target.value)}
                />
              </Box>

              <Box mb={4}>
                <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                  Other Notes
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  variant="outlined"
                  value={reportData.otherNotes}
                  onChange={(e) => handleInputChange(null, 'otherNotes', e.target.value)}
                />
              </Box>

              <Box mb={4}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Assistive Device</FormLabel>
                  <RadioGroup
                    row
                    value={reportData.assistiveDeviceUsed ? 'yes' : 'no'}
                    onChange={(e) => {
                      const isUsed = e.target.value === 'yes';
                      setReportData(prev => ({
                        ...prev,
                        assistiveDeviceUsed: isUsed,
                        assistiveDeviceType: isUsed ? prev.assistiveDeviceType : '',
                        objectiveStatement: generateObjectiveStatement(selectedTrialIds)
                      }))
                    }}
                  >
                    <FormControlLabel value="yes" control={<Radio />} label="With assistive device" />
                    <FormControlLabel value="no" control={<Radio />} label="Without assistive device" />
                  </RadioGroup>
                </FormControl>
                
                {reportData.assistiveDeviceUsed && (
                  <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel id="device-type-label">Device Type</InputLabel>
                    <Select
                      labelId="device-type-label"
                      value={reportData.assistiveDeviceType}
                      label="Device Type"
                      onChange={(e) => {
                        handleInputChange(null, 'assistiveDeviceType', e.target.value);
                        // Update objective statement when device type changes
                        setReportData(prev => ({
                          ...prev,
                          assistiveDeviceType: e.target.value,
                          objectiveStatement: generateObjectiveStatement(selectedTrialIds)
                        }));
                      }}
                    >
                      <MenuItem value="cane">Cane</MenuItem>
                      <MenuItem value="walker">Walker</MenuItem>
                      <MenuItem value="crutches">Crutches</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </FormControl>
                )}
              </Box>
            </Box>
          )}

          <Box display="flex" justifyContent="space-between" mt={3}>
            <Button
              variant="outlined"
              onClick={activeStep === 0 ? handleClose : handleBack}
            >
              {activeStep === 0 ? 'Cancel' : 'Back'}
            </Button>
            <Box>
              {activeStep === steps.length - 1 && (
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleGenerateReport}
                  sx={{ mr: 2 }}
                >
                  Generate Report
                </Button>
              )}
              {activeStep < steps.length - 1 && (
                <Button variant="contained" onClick={handleNext}>
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      </Modal>

      {/* PDF Download Modal */}
      <Modal
        open={showPdfModal}
        onClose={handleClosePdfModal}
        aria-labelledby="pdf-download-modal"
      >
        <Paper
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '80%', sm: '500px' },
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            boxShadow: 24,
            borderRadius: 2
          }}
        >
          <Typography variant="h6" component="h2" align="center">
            Your Report is Ready
          </Typography>
          
          <PDFDownloadLink 
            document={
              <ReportPDFDocument 
                patientName={formattedPatientName} 
                clinicianName={clinicianName}
                reportData={reportData} 
                generatedDate={new Date()} 
                assistiveDeviceInfo={assistiveDeviceInfo}
              />
            } 
            fileName={`${formattedPatientName.replace(/\s+/g, '_')}_Clinical_Report_${new Date().toISOString().slice(0,10)}.pdf`}
            style={{
              textDecoration: 'none'
            }}
          >
            {({ blob, url, loading, error }) => (
              <Button 
                variant="contained" 
                color="primary" 
                fullWidth
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Preparing Report...' : 'Download PDF Report'}
              </Button>
            )}
          </PDFDownloadLink>
          
          <Button variant="outlined" onClick={handleClosePdfModal}>
            Close
          </Button>
        </Paper>
      </Modal>
    </>
  );
};

export default ReportGenerator; 